import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, setToken, getToken } from "../lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      return r.data;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    const t = getToken();
    if (!t) { setLoading(false); return; }
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setToken(null))
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    setToken(r.data.token);
    setUser(r.data.user);
    return r.data.user;
  }, []);

  const register = useCallback(async (email, password, name, referral_code, disclaimer_acknowledged) => {
    const r = await api.post("/auth/register", { email, password, name, referral_code, disclaimer_acknowledged });
    setToken(r.data.token);
    setUser(r.data.user);
    return r.data.user;
  }, []);

  // Adopt a token issued by an alternate flow (e.g. passkey sign-in)
  const adoptSession = useCallback((token, userObj) => {
    setToken(token);
    setUser(userObj);
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
  }, []);

  // Memoise the context value so consumers don't re-render every parent render
  const value = useMemo(
    () => ({ user, loading, login, register, logout, refreshUser, adoptSession }),
    [user, loading, login, register, logout, refreshUser, adoptSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
